import { Camera, X, RotateCcw, Aperture, Image, Check } from 'lucide-react';

/**
 * Camera Modal — full-screen native camera capture for inspection photos.
 * Pure presentation; the parent owns the video stream, captured image
 * state, and refs.
 *
 * Extracted from CleaningInspection.js as part of the giant-component
 * refactor (Feb 2026). Behavior is byte-for-byte equivalent.
 */
const InspectionCameraModal = ({
  open,
  capturedImage,
  cameraStream,
  isCompressing,
  videoRef,
  onClose,
  onSwitchCamera,
  onCapture,
  onRetake,
  onSave,
  onOpenGallery,
}) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.9)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
        padding: '20px',
      }}
      data-testid="inspection-camera-modal"
    >
      {/* Camera Header */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
        }}
      >
        <h3 style={{ color: 'white', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Camera size={20} /> Take Photo
        </h3>
        <button
          onClick={onClose}
          data-testid="close-camera-modal"
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
          }}
        >
          <X size={24} />
        </button>
      </div>

      {/* Camera Preview / Captured Image */}
      <div
        style={{
          width: '100%',
          maxWidth: '640px',
          aspectRatio: '4/3',
          borderRadius: '16px',
          overflow: 'hidden',
          background: '#000',
          position: 'relative',
        }}
      >
        {!capturedImage ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <img
            src={capturedImage}
            alt="Captured"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        {!capturedImage && !cameraStream && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              textAlign: 'center',
            }}
          >
            <div
              className="spinner"
              style={{
                width: '40px',
                height: '40px',
                border: '3px solid rgba(255,255,255,0.3)',
                borderTopColor: 'white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 12px',
              }}
            />
            <p>Starting camera...</p>
          </div>
        )}
      </div>

      {/* Camera Controls */}
      <div style={{ marginTop: '24px', display: 'flex', gap: '16px', alignItems: 'center' }}>
        {!capturedImage ? (
          <>
            <button
              onClick={onSwitchCamera}
              data-testid="switch-camera-button"
              title="Switch Camera"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
              }}
            >
              <RotateCcw size={24} />
            </button>

            <button
              onClick={onCapture}
              disabled={!cameraStream || isCompressing}
              data-testid="capture-photo-button"
              title="Take Photo"
              style={{
                background: 'white',
                border: '4px solid rgba(255,255,255,0.5)',
                borderRadius: '50%',
                width: '72px',
                height: '72px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: cameraStream && !isCompressing ? 'pointer' : 'not-allowed',
                opacity: cameraStream && !isCompressing ? 1 : 0.5,
              }}
            >
              {isCompressing ? (
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    border: '3px solid #e5e7eb',
                    borderTopColor: '#0073ea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : (
                <Aperture size={32} color="#0073ea" />
              )}
            </button>

            <button
              onClick={onOpenGallery}
              data-testid="open-gallery-button"
              title="Upload from Gallery"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
              }}
            >
              <Image size={24} />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onRetake}
              data-testid="retake-photo-button"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 28px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: 'white',
                fontWeight: '600',
                fontSize: '16px',
              }}
            >
              <RotateCcw size={20} /> Retake
            </button>

            <button
              onClick={onSave}
              data-testid="save-photo-button"
              style={{
                background: '#22c55e',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 28px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: 'white',
                fontWeight: '600',
                fontSize: '16px',
              }}
            >
              <Check size={20} /> Use Photo
            </button>
          </>
        )}
      </div>

      <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '16px', fontSize: '14px', textAlign: 'center' }}>
        {isCompressing
          ? '⏳ Optimizing image...'
          : !capturedImage
          ? 'Point camera at the area to inspect and tap the capture button'
          : 'Review your photo and tap "Use Photo" to save'}
      </p>
    </div>
  );
};

export default InspectionCameraModal;
